import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  format,
  startOfWeek,
  endOfWeek,
  addDays,
  addWeeks,
  subWeeks,
  isSameDay,
  isToday as isDateToday,
} from 'date-fns';
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  VideoCameraIcon,
  XMarkIcon,
  UserIcon,
  BriefcaseIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import http from '../lib/http';

/* ═══════════════════════════════════════════════════════════════════
   Constants
   ═══════════════════════════════════════════════════════════════════ */
const HOUR_HEIGHT = 60;
const START_HOUR = 7;
const END_HOUR = 22;
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);

const VIRTUAL_EVENT_COLOR = '#1a73e8';
const IN_PERSON_EVENT_COLOR = '#d97706';

/* ═══════════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════════ */
function getEventColor(event) {
  return event?.isInPerson ? IN_PERSON_EVENT_COLOR : VIRTUAL_EVENT_COLOR;
}

function eventPosition(event) {
  const s = new Date(event.startTime);
  const e = new Date(event.endTime);
  const sMin = s.getHours() * 60 + s.getMinutes();
  const eMin = e.getHours() * 60 + e.getMinutes();
  const top = ((sMin - START_HOUR * 60) / 60) * HOUR_HEIGHT;
  const height = Math.max(((eMin - sMin) / 60) * HOUR_HEIGHT, 22);
  return { top, height };
}

function layoutColumns(events) {
  if (!events.length) return [];
  const sorted = [...events].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  const cols = [];

  for (const ev of sorted) {
    const evStart = new Date(ev.startTime).getTime();
    let placed = false;
    for (let c = 0; c < cols.length; c++) {
      const last = cols[c][cols[c].length - 1];
      if (new Date(last.endTime).getTime() <= evStart) {
        cols[c].push(ev);
        placed = true;
        break;
      }
    }
    if (!placed) cols.push([ev]);
  }

  const total = cols.length;
  return cols.flatMap((col, ci) =>
    col.map((ev) => ({ ...ev, _ci: ci, _total: total })),
  );
}

/* ═══════════════════════════════════════════════════════════════════
   CalendarPage
   ═══════════════════════════════════════════════════════════════════ */
export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refDate, setRefDate] = useState(new Date());
  const [cancellingId, setCancellingId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [popupPos, setPopupPos] = useState(null);
  const [now, setNow] = useState(new Date());

  const gridRef = useRef(null);
  const popupRef = useRef(null);
  const didScroll = useRef(false);

  /* ── Week boundaries (Sun → Sat) ─────────────────────────────── */
  const weekStart = useMemo(() => startOfWeek(refDate, { weekStartsOn: 0 }), [refDate]);
  const weekEnd = useMemo(() => endOfWeek(refDate, { weekStartsOn: 0 }), [refDate]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  /* ── Data fetching ───────────────────────────────────────────── */
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await http.get('/api/scheduling/calendar', {
        params: { startDate: weekStart.toISOString(), endDate: weekEnd.toISOString() },
      });
      setEvents(res.data.events || []);
    } catch {
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [weekStart, weekEnd]);

  useEffect(() => {
    fetchEvents();
    didScroll.current = false;
  }, [fetchEvents]);

  /* ── Clock tick (update red line every minute) ───────────────── */
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const nowTop = useMemo(() => {
    const m = now.getHours() * 60 + now.getMinutes();
    return ((m - START_HOUR * 60) / 60) * HOUR_HEIGHT;
  }, [now]);

  /* ── Auto-scroll to "now" once after load ────────────────────── */
  useEffect(() => {
    if (gridRef.current && !loading && !didScroll.current) {
      const target = Math.max((now.getHours() - START_HOUR - 1) * HOUR_HEIGHT, 0);
      gridRef.current.scrollTop = target;
      didScroll.current = true;
    }
  }, [loading, now]);

  /* ── Close popup on outside click / Escape ───────────────────── */
  useEffect(() => {
    if (!selected) return;
    const onClick = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) setSelected(null);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setSelected(null);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [selected]);

  /* ── Navigation ──────────────────────────────────────────────── */
  const goToday = () => setRefDate(new Date());
  const goPrev = () => setRefDate((d) => subWeeks(d, 1));
  const goNext = () => setRefDate((d) => addWeeks(d, 1));

  /* ── Actions ─────────────────────────────────────────────────── */
  const handleExport = async () => {
    try {
      const res = await http.get('/api/scheduling/calendar/export', {
        params: { startDate: weekStart.toISOString(), endDate: weekEnd.toISOString() },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'interviews.ics';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Calendar file downloaded');
    } catch {
      toast.error('Failed to export calendar');
    }
  };

  const handleCancel = async (slotId) => {
    if (!window.confirm('Cancel this interview? The candidate will be notified.')) return;
    setCancellingId(slotId);
    try {
      await http.patch(`/api/scheduling/slot/${slotId}/cancel`);
      toast.success('Interview cancelled');
      setSelected(null);
      fetchEvents();
    } catch {
      toast.error('Failed to cancel');
    } finally {
      setCancellingId(null);
    }
  };

  const openSlotIcs = (slotId) => {
    window.open(
      `${process.env.REACT_APP_API_URL || ''}/api/scheduling/slot/${slotId}/ics`,
      '_blank',
    );
  };

  /* ── Event click → popup ─────────────────────────────────────── */
  const openPopup = (event, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const PW = 370;
    const PH = 420;

    let left = rect.right + 10;
    let top = rect.top;

    if (left + PW > window.innerWidth - 16) left = rect.left - PW - 10;
    if (left < 16) left = 16;
    if (top + PH > window.innerHeight - 16) top = window.innerHeight - PH - 16;
    if (top < 16) top = 16;

    setPopupPos({ top, left });
    setSelected(event);
  };

  /* ── Per-day events ──────────────────────────────────────────── */
  const eventsForDay = useCallback(
    (day) => events.filter((ev) => isSameDay(new Date(ev.startTime), day)),
    [events],
  );

  const hasEvents = events.length > 0;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  /* ═══════════════════════════════════════════════════════════════
     Render
     ═══════════════════════════════════════════════════════════════ */
  return (
    <div className="flex flex-col -mx-1" style={{ height: 'calc(100vh - 3rem)' }}>
      {/* ─── Top Toolbar ───────────────────────────────────────── */}
      <div className="flex items-center justify-between shrink-0 mb-3">
        <div className="flex items-center gap-3">
          <button
            onClick={goToday}
            className="h-9 px-4 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-full hover:bg-slate-50 hover:shadow-sm transition-all"
          >
            Today
          </button>

          <div className="flex items-center">
            <button
              onClick={goPrev}
              className="p-1.5 rounded-full text-slate-500 hover:bg-slate-100 transition-colors"
              aria-label="Previous week"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              onClick={goNext}
              className="p-1.5 rounded-full text-slate-500 hover:bg-slate-100 transition-colors"
              aria-label="Next week"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>

          <h1 className="text-xl font-normal text-slate-800 select-none">
            {format(weekStart, 'MMMM yyyy')}
          </h1>
        </div>

        <div className="flex items-center gap-2">
          {hasEvents && (
            <div className="hidden md:flex items-center gap-2 mr-1">
              <span className="inline-flex items-center gap-1.5 h-8 px-2.5 text-[11px] font-medium text-slate-600 bg-white border border-slate-300 rounded-full select-none">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: VIRTUAL_EVENT_COLOR }} />
                Virtual
              </span>
              <span className="inline-flex items-center gap-1.5 h-8 px-2.5 text-[11px] font-medium text-slate-600 bg-white border border-slate-300 rounded-full select-none">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: IN_PERSON_EVENT_COLOR }} />
                In-person
              </span>
            </div>
          )}
          {hasEvents && (
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-1.5 h-9 px-3.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-full hover:bg-slate-50 hover:shadow-sm transition-all"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
              Export .ics
            </button>
          )}

          <span className="h-9 px-3.5 flex items-center text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-full select-none">
            Week
          </span>
        </div>
      </div>

      {/* ─── Calendar Container ────────────────────────────────── */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="inline-block w-5 h-5 border-2 border-slate-200 border-t-[#1a73e8] rounded-full animate-spin" />
            <span className="text-sm text-slate-500">Loading calendar…</span>
          </div>
        </div>
      ) : (
        <div className="flex-1 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-0">
          {/* ── Day Header Row ────────────────────────────────── */}
          <div
            className="grid shrink-0 border-b border-slate-200"
            style={{ gridTemplateColumns: '56px repeat(7, 1fr)' }}
          >
            {/* Timezone label */}
            <div className="flex items-end justify-center pb-2 border-r border-slate-100">
              <span className="text-[10px] text-slate-400 leading-none select-none">
                {tz.replace('_', ' ').split('/').pop()}
              </span>
            </div>

            {days.map((day) => {
              const today = isDateToday(day);
              return (
                <div
                  key={day.toISOString()}
                  className="py-2 text-center border-r border-slate-100 last:border-r-0"
                >
                  <div
                    className={`text-[11px] font-semibold uppercase tracking-wider ${
                      today ? 'text-[#1a73e8]' : 'text-slate-400'
                    }`}
                  >
                    {format(day, 'EEE')}
                  </div>
                  <div className="flex justify-center mt-0.5">
                    <span
                      className={`text-[26px] font-normal leading-none transition-colors ${
                        today
                          ? 'bg-[#1a73e8] text-white w-11 h-11 flex items-center justify-center rounded-full'
                          : 'text-slate-800 w-11 h-11 flex items-center justify-center'
                      }`}
                    >
                      {format(day, 'd')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Scrollable Time Grid ─────────────────────────── */}
          <div ref={gridRef} className="flex-1 overflow-y-auto min-h-0 relative">
            <div
              className="grid"
              style={{
                gridTemplateColumns: '56px repeat(7, 1fr)',
                height: HOURS.length * HOUR_HEIGHT,
              }}
            >
              {/* Time Labels */}
              <div className="relative border-r border-slate-100">
                {HOURS.map((h) => (
                  <div
                    key={h}
                    className="absolute w-full pr-2 text-right"
                    style={{ top: (h - START_HOUR) * HOUR_HEIGHT - 7 }}
                  >
                    <span className="text-[11px] text-slate-400 font-normal leading-none select-none">
                      {format(new Date(2000, 0, 1, h), 'h a')}
                    </span>
                  </div>
                ))}
              </div>

              {/* Day Columns */}
              {days.map((day) => {
                const dayEvts = eventsForDay(day);
                const laid = layoutColumns(dayEvts);
                const today = isDateToday(day);

                return (
                  <div
                    key={day.toISOString()}
                    className={`relative border-r border-slate-100 last:border-r-0 ${
                      today ? 'bg-[#e8f0fe]/25' : ''
                    }`}
                    onClick={() => setSelected(null)}
                  >
                    {/* Hour grid lines */}
                    {HOURS.map((h) => (
                      <div
                        key={h}
                        className="absolute w-full border-t border-slate-100"
                        style={{ top: (h - START_HOUR) * HOUR_HEIGHT }}
                      />
                    ))}
                    {/* Half-hour lines */}
                    {HOURS.map((h) => (
                      <div
                        key={`hh-${h}`}
                        className="absolute w-full border-t border-slate-50"
                        style={{ top: (h - START_HOUR) * HOUR_HEIGHT + HOUR_HEIGHT / 2 }}
                      />
                    ))}

                    {/* Events */}
                    {laid.map((ev) => {
                      const pos = eventPosition(ev);
                      const color = getEventColor(ev);
                      const w = 100 / ev._total;
                      const l = (ev._ci / ev._total) * 100;
                      const isShort = pos.height < 38;
                      const isActive = selected?.id === ev.id;

                      return (
                        <div
                          key={ev.id}
                          className={`absolute rounded-md cursor-pointer overflow-hidden transition-all duration-100 ${
                            isActive
                              ? 'ring-2 ring-offset-1 ring-slate-400 shadow-lg z-30'
                              : 'hover:brightness-90 hover:shadow-md z-10'
                          }`}
                          style={{
                            top: pos.top + 1,
                            height: pos.height - 2,
                            left: `calc(${l}% + 2px)`,
                            width: `calc(${w}% - 4px)`,
                            backgroundColor: color,
                          }}
                          onClick={(e) => openPopup(ev, e)}
                        >
                          <div className="px-2 py-1 h-full select-none">
                            {isShort ? (
                              <p className="text-[11px] font-medium text-white truncate leading-snug">
                                {format(new Date(ev.startTime), 'h:mm')}{' '}
                                {ev.candidateName || 'Interview'}
                              </p>
                            ) : (
                              <>
                                <p className="text-[11px] font-semibold text-white truncate leading-snug">
                                  {ev.jobTitle
                                    ? `Interview: ${ev.jobTitle}`
                                    : ev.candidateName || 'Interview'}
                                </p>
                                <p className="text-[10px] text-white/90 truncate mt-px">
                                  {format(new Date(ev.startTime), 'h:mm')} –{' '}
                                  {format(new Date(ev.endTime), 'h:mm a')}
                                </p>
                                {pos.height >= 68 && (
                                  <p className="text-[10px] text-white/80 truncate mt-px">
                                    {ev.isInPerson ? 'In-person interview' : 'Virtual interview'}
                                  </p>
                                )}
                                {pos.height >= 56 && ev.candidateName && (
                                  <p className="text-[10px] text-white/75 truncate mt-px">
                                    {ev.candidateName}
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* ── Current time indicator (red line) ─── */}
                    {today && nowTop >= 0 && nowTop <= HOURS.length * HOUR_HEIGHT && (
                      <div
                        className="absolute left-0 right-0 z-20 pointer-events-none"
                        style={{ top: nowTop }}
                      >
                        <div className="relative flex items-center">
                          <div className="absolute -left-[5px] w-[10px] h-[10px] bg-[#ea4335] rounded-full shadow-sm" />
                          <div className="w-full h-[2px] bg-[#ea4335]" />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
         Event Detail Popup
         ═══════════════════════════════════════════════════════════ */}
      {selected && popupPos && (
        <div
          ref={popupRef}
          className="fixed z-50 bg-white rounded-2xl overflow-hidden"
          style={{
            top: popupPos.top,
            left: popupPos.left,
            width: 370,
            boxShadow:
              '0 24px 38px 3px rgba(0,0,0,.14), 0 9px 46px 8px rgba(0,0,0,.12), 0 11px 15px -7px rgba(0,0,0,.2)',
            animation: 'gcalPopup 150ms cubic-bezier(.2,0,.13,1.5)',
          }}
        >
          {/* Header actions */}
          <div className="flex items-center justify-end gap-0.5 px-2 pt-2">
            <button
              onClick={() => openSlotIcs(selected.id)}
              className="p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              title="Download .ics"
            >
              <CalendarDaysIcon className="h-[18px] w-[18px]" />
            </button>
            <button
              onClick={() => handleCancel(selected.id)}
              disabled={cancellingId === selected.id}
              className="p-2 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
              title="Cancel interview"
            >
              <TrashIcon className="h-[18px] w-[18px]" />
            </button>
            <button
              onClick={() => setSelected(null)}
              className="p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <XMarkIcon className="h-[18px] w-[18px]" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 pb-5 -mt-1">
            {/* Title */}
            <div className="flex items-start gap-3.5 mb-4">
              <span
                className="w-[14px] h-[14px] rounded-sm mt-1.5 shrink-0"
                style={{ backgroundColor: getEventColor(selected) }}
              />
              <div className="min-w-0">
                <h3 className="text-[22px] font-normal text-slate-900 leading-snug truncate">
                  Interview: {selected.jobTitle || 'Scheduled'}
                </h3>
                <p className="text-[13px] text-slate-600 mt-1">
                  {format(new Date(selected.startTime), 'EEEE, MMMM d')} &middot;{' '}
                  {format(new Date(selected.startTime), 'h:mm')} –{' '}
                  {format(new Date(selected.endTime), 'h:mm a')}
                </p>
                <p className="text-[12px] font-medium mt-1 text-slate-700">
                  {selected.isInPerson ? 'Mode: In-person interview' : 'Mode: Virtual interview'}
                </p>
              </div>
            </div>

            {/* Meeting link */}
            {selected.meetingLink && (
              <div className="flex items-start gap-3.5 py-3 border-t border-slate-100">
                <VideoCameraIcon className="h-5 w-5 text-[#1a73e8] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <a
                    href={selected.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[13px] font-medium text-[#1a73e8] hover:underline"
                  >
                    Join with Google Meet
                  </a>
                  <p className="text-[11px] text-slate-400 truncate mt-0.5">
                    {selected.meetingLink}
                  </p>
                </div>
              </div>
            )}
            {selected.isInPerson && selected.inPersonAddress && (
              <div className="flex items-start gap-3.5 py-3 border-t border-slate-100">
                <div className="h-5 w-5 mt-0.5 shrink-0 rounded bg-amber-100 text-amber-700 text-[10px] font-bold flex items-center justify-center">
                  IP
                </div>
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-slate-800">Interview Address</p>
                  <p className="text-[12px] text-slate-500 whitespace-pre-line">{selected.inPersonAddress}</p>
                </div>
              </div>
            )}

            {/* Candidate */}
            <div className="flex items-start gap-3.5 py-3 border-t border-slate-100">
              <UserIcon className="h-5 w-5 text-slate-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-slate-800">
                  {selected.candidateName || 'Candidate'}
                </p>
                {selected.candidateEmail && (
                  <p className="text-[12px] text-slate-500 truncate">{selected.candidateEmail}</p>
                )}
              </div>
            </div>

            {/* Job / description */}
            {selected.jobTitle && (
              <div className="flex items-start gap-3.5 py-3 border-t border-slate-100">
                <BriefcaseIcon className="h-5 w-5 text-slate-400 mt-0.5 shrink-0" />
                <p className="text-[13px] text-slate-600 leading-relaxed">
                  Interview with {selected.candidateName || 'candidate'} for{' '}
                  <span className="font-medium text-slate-800">{selected.jobTitle}</span>
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
              {selected.meetingLink && (
                <a
                  href={selected.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 text-[13px] font-medium text-white bg-[#1a73e8] rounded-full hover:bg-[#1557b0] transition-colors shadow-sm"
                >
                  <VideoCameraIcon className="h-4 w-4" />
                  Join Meeting
                </a>
              )}
              <button
                onClick={() => openSlotIcs(selected.id)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 text-[13px] font-medium text-slate-700 bg-white border border-slate-300 rounded-full hover:bg-slate-50 transition-colors"
              >
                <CalendarDaysIcon className="h-4 w-4" />
                Add to Calendar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup animation keyframes */}
      <style>{`
        @keyframes gcalPopup {
          from { opacity: 0; transform: scale(.96) translateY(4px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
